import * as Headless from '@headlessui/react'
import clsx from 'clsx'

import { Text } from './text'
import { Input } from './input'
import { Textarea } from './textarea'
import { Listbox } from './listbox'

const sizes = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
}

export function DialogCloseButton({ onClick }) {
  return (
    <button
      type="button"
      className="absolute right-4 top-4 rounded-lg p-1 text-zinc-500 hover:text-zinc-600 dark:text-zinc-400 dark:hover:text-zinc-300"
      onClick={onClick}
    >
      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
      </svg>
    </button>
  )
}

export function Dialog({ size = 'lg', className, children, onClose, ...props }) {
  return (
    <Headless.Dialog {...props} onClose={onClose}>
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 px-2 py-2 transition duration-100 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16 dark:bg-zinc-950/50"
      />

      <div className="fixed inset-0 w-screen overflow-y-auto pt-6 sm:pt-0">
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
          <Headless.DialogPanel
            transition
            className={clsx(
              className,
              sizes[size],
              'relative row-start-2 w-full min-w-0 rounded-t-3xl bg-white p-[--gutter] shadow-lg ring-1 ring-zinc-950/10 [--gutter:theme(spacing.8)] sm:mb-auto sm:rounded-2xl dark:bg-zinc-900 dark:ring-white/10 forced-colors:outline',
              'transition duration-100 will-change-transform data-[closed]:translate-y-12 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:data-[closed]:translate-y-0 sm:data-[closed]:data-[enter]:scale-95'
            )}
          >
            <DialogCloseButton onClick={onClose} />
            {children}
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  )
}

export function DialogTitle({ className, ...props }) {
  return (
    <Headless.DialogTitle
      {...props}
      className={clsx(className, 'text-balance text-lg/6 font-semibold text-zinc-950 sm:text-base/6 dark:text-white')}
    />
  )
}

export function DialogDescription({ className, ...props }) {
  return <Headless.Description as={Text} {...props} className={clsx(className, 'mt-2 text-pretty')} />
}

export function DialogBody({ className, ...props }) {
  return <div {...props} className={clsx(className, 'mt-6')} />
}

export function DialogActions({ className, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'mt-8 flex flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto'
      )}
    />
  )
}

export function DialogLabel({ children, className }) {
  return (
    <label className={clsx("text-sm font-medium text-zinc-500 dark:text-zinc-400", className)}>
      {children}
    </label>
  )
}

export function DialogInput({ label, className, ...props }) {
  return (
    <div className={clsx("space-y-2", className)}>
      <DialogLabel>{label}</DialogLabel>
      <div className="mt-1">
        <Input {...props} />
      </div>
    </div>
  )
}

export function DialogTextarea({ label, className, ...props }) {
  return (
    <div className={clsx("space-y-2", className)}>
      <DialogLabel>{label}</DialogLabel>
      <div className="mt-1">
        <Textarea {...props} />
      </div>
    </div>
  )
}

export function DialogSelect({ label, className, children, ...props }) {
  return (
    <div className={clsx("space-y-2", className)}>
      <DialogLabel>{label}</DialogLabel>
      <div className="mt-1">
        <Listbox {...props}>
          {children}
        </Listbox>
      </div>
    </div>
  )
}

export function DialogInfoRow({ label, value, className }) {
  if (!value) return null;
  
  return (
    <div className={className}>
      <dt className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{label}</dt>
      <dd className="mt-1 text-sm text-zinc-950 dark:text-white">{value}</dd>
    </div>
  )
}

export function DialogInfoGrid({ children, className }) {
  return (
    <div className={clsx("grid grid-cols-1 gap-y-4 sm:grid-cols-1 sm:gap-x-6", className)}>
      {children}
    </div>
  )
}